import { VatSupportedModelType } from '@teinor/erp/types/company/accounting/vatSupported'
import { connect, ConnectedProps } from 'react-redux'
import { ComponentTabObject } from '../../../../../../../../../utils/hooks/useTabLogic'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { InputTableLeftTabsProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { SupplierPayload, SuppliersResult } from '../../../../../../../../../utils/reducers/products/suppliers'
import { useInvoiceType, useRegimeType } from '../../../../../../../../../utils/hooks/useTypeIdNumber'
import { FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import ITHeaderText from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { AllReduxPayloads } from '../../../../../../../../../utils/reducers'
import withLoading from '../../../../../../../../../utils/hoc/withLoading'
import { returnClassOfSpanTemplate } from '../../../../../../../../../utils/globals/tailwind'
import { displayDate } from '../../../../../../../../../utils/globals/date'
import { useMemo } from 'react'
import { parseAddressToOneLine } from '../../../../../../../../../utils/globals/address'
import AppStyleDecider from '../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import { useOrderITHeaderHandleChangeLogic } from '../../../../../../../../../specificComponents/inputTableLayouts/order/useOrderITHeaderHandleChange'

type HeaderVatSupportedFinishedProps = InputTableLeftColumnCompleteRequiredProps & ReduxHeaderVatSupportedFinished & {}

const HeaderVatSupportedFinished: React.FC<HeaderVatSupportedFinishedProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={HeaderVatSupportedFinishedDefault} NewComponent={HeaderVatSupportedFinishedNew} {...rest} />
}

const HeaderVatSupportedFinishedDefault: React.FC<HeaderVatSupportedFinishedProps> = ({ suppliers, updateDataURL, ...rest }) => {
    const { t } = useTwinTranslation()

    const tabs: ComponentTabObject = {
        'main': {
            component: HeaderVatSupportedFinishedMain,
            text: t('dataVatSupported', 'Datos del IVA Soportado'),
            extraProps: { suppliers }
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} updateDataURL={updateDataURL} {...rest}/>
    )
}

const HeaderVatSupportedFinishedNew: React.FC<HeaderVatSupportedFinishedProps> = ({ suppliers, headerData, getCompleteData, setCompleteData, updateDataURL }) => {
    const { t } = useTwinTranslation()
    const { invoiceTypes } = useInvoiceType()
    const { regimeTypes } = useRegimeType()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData, updateDataURL })
    const memorizedAddress = useMemo(() => parseAddressToOneLine(headerData?.WorkingCenterAddress), [headerData?.WorkingCenterAddress])
    const sections: FormRender<VatSupportedModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'SupplierId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplier', 'Proveedor')} value={suppliers?.all?.[value]?.name || ''} />
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('nAssigned', 'Nº Asignado')} value={value} />,
                },
                {
                    name: 'supplier_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateSupplier', 'Fecha del proveedor')} value={value ? displayDate(new Date(value)) : ''} />
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('numSupplier', 'Num. proveedor')} value={value} />
                },
                {
                    name: 'type',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('invoiceType', 'Tipo de factura')} value={invoiceTypes[value]?.name} />
                },
                {
                    name: 'regime',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('regime', 'Régimen')} value={regimeTypes[value]?.name} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('vatSupportedDate', 'Fecha IVA Sop.')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billedTo', 'Facturado a')} value={headerData?.BillingWorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'BillingWorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} />,
                    hideSectionByDefault: true
                },
                {
                    //@ts-ignore
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={extraProps.workingCenterAddress} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddress
                    },
                    hideSectionByDefault: true
                },
            ]
        }
    ]
    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
    )
}

interface HeaderVatSupportedFinishedMainProps extends InputTableLeftTabsProps {
    suppliers: SuppliersResult
}

const HeaderVatSupportedFinishedMain: React.FC<HeaderVatSupportedFinishedMainProps> = ({ suppliers, handleChange, headerData }) => {
    const { t } = useTwinTranslation()
    const { invoiceTypes } = useInvoiceType()
    const { regimeTypes } = useRegimeType()
    const memorizedAddress = useMemo(() => parseAddressToOneLine(headerData?.WorkingCenterAddress), [headerData?.WorkingCenterAddress])
    const sections: FormRender<VatSupportedModelType> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'SupplierId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplier', 'Proveedor')} value={suppliers?.all?.[value]?.name} className={returnClassOfSpanTemplate(1)}/>
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('nAssigned', 'Nº Asignado')} value={value} className={returnClassOfSpanTemplate(1)}/>,
                },
                {
                    name: 'supplier_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateSupplier', 'Fecha del proveedor')} value={value ? displayDate(new Date(value)) : ''} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('numSupplier', 'Num. proveedor')} value={value} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'type',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('invoiceType', 'Tipo de factura')} value={invoiceTypes[value]?.name} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'regime',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('regime', 'Régimen')} value={regimeTypes[value]?.name} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('vatSupportedDate', 'Fecha IVA Sop.')} value={displayDate(new Date(value))} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billedTo', 'Facturado a')} value={headerData?.BillingWorkingCenter?.name || ''} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    name: 'BillingWorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} className={returnClassOfSpanTemplate(1)} />
                },
                {
                    //@ts-ignore
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={extraProps.workingCenterAddress} className={returnClassOfSpanTemplate(2)} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddress
                    }
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
    )
}


const headerVatSupportedFinishedDispatch = {
    setSuppliers: (payload: SupplierPayload) => ({ type: 'CHANGE_SUPPLIER', payload }),
}

export type ReduxHeaderVatSupportedFinished = ConnectedProps<typeof headerVatSupportedFinishedConnect>
const mapHeaderVatSupportedFinishedConnector = (state: AllReduxPayloads) => ({ suppliers: state.suppliers, warehouses: state.warehouses, })
const headerVatSupportedFinishedConnect = connect(mapHeaderVatSupportedFinishedConnector, headerVatSupportedFinishedDispatch)
const headerVatSupportedFinishedConnectLoading = withLoading(HeaderVatSupportedFinished, [{ fetchUrl: '/api/app/supplier/getAllSuppliersComplete', propName: 'suppliers', setFunctionName: 'setSuppliers' }])
const HeaderVatSupportedFinishedConnect = headerVatSupportedFinishedConnect(headerVatSupportedFinishedConnectLoading)

export default HeaderVatSupportedFinishedConnect